import { ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface ReorderControlsProps<T> {
  item: T;
  items: T[];
  onReorder: (newItems: T[]) => void;
  getItemId: (item: T) => string;
  disabled?: boolean;
  isAnyReordering?: boolean; // True if ANY item in the list is being reordered
}

/**
 * ReorderControls - Simple up/down arrow reordering
 * Reliable alternative to drag-and-drop that works on all devices
 * All controls disabled when any reordering is in progress
 */
export function ReorderControls<T>({
  item,
  items,
  onReorder,
  getItemId,
  disabled = false,
  isAnyReordering = false
}: ReorderControlsProps<T>) {
  const currentIndex = items.findIndex(i => getItemId(i) === getItemId(item));
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === items.length - 1;

  const moveUp = () => {
    if (isFirst || disabled || isAnyReordering) return;
    
    const newItems = [...items];
    [newItems[currentIndex], newItems[currentIndex - 1]] = 
    [newItems[currentIndex - 1], newItems[currentIndex]];
    
    // Update order properties
    const reorderedItems = newItems.map((itm, index) => ({
      ...itm,
      order: index
    }));
    
    onReorder(reorderedItems);
  };

  const moveDown = () => {
    if (isLast || disabled || isAnyReordering) return;
    
    const newItems = [...items];
    [newItems[currentIndex], newItems[currentIndex + 1]] = 
    [newItems[currentIndex + 1], newItems[currentIndex]];
    
    // Update order properties
    const reorderedItems = newItems.map((itm, index) => ({
      ...itm,
      order: index
    }));
    
    onReorder(reorderedItems);
  };

  // Don't show controls if there's only one item or controls are disabled
  if (items.length <= 1 || disabled) return null;

  return (
    <div className="flex items-center space-x-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={moveUp}
        disabled={isFirst || disabled || isAnyReordering}
        className="p-1"
        title={isAnyReordering ? "Reordering in progress..." : "Move up"}
      >
        <ChevronUp className="w-4 h-4" />
      </Button>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={moveDown}
        disabled={isLast || disabled || isAnyReordering}
        className="p-1" 
        title={isAnyReordering ? "Reordering in progress..." : "Move down"}
      >
        <ChevronDown className="w-4 h-4" />
      </Button>
    </div>
  );
}

/**
 * CardLoadingOverlay - Shows loading spinner overlay on entire card
 * Uses theme color for the spinner with improved corner radius handling
 */
export function CardLoadingOverlay({ 
  loading, 
  rounded = "rounded-xl" 
}: { 
  loading: boolean;
  rounded?: string;
}) {
  if (!loading) return null;

  return (
    <div className={`absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10 ${rounded}`}>
      <div className="flex flex-col items-center space-y-2">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <span className="text-sm text-gray-600 font-medium">Reordering...</span>
      </div>
    </div>
  );
}